import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Key, Layers, ArrowUpRight, AlertTriangle, Clock, FileText, Database, Server, Lock, UserCheck, UserX, AlertCircle, ArrowRight, LayoutDashboard } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../components/Card';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { useAdminStore } from '../../store/adminStore';
import { AdminPageHeader, AdminMetricCard } from './components';
import toast from 'react-hot-toast';

export function AdminDashboard() {
  const navigate = useNavigate();
  const { 
    stats, systemHealth, auditLogs, users,
    loadStats, loadSystemHealth, loadAuditLogs, loadUsers, isLoading 
  } = useAdminStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  useEffect(() => {
    loadStats();
    loadSystemHealth();
    loadAuditLogs({ limit: 8 });
    loadUsers();
  }, [loadStats, loadSystemHealth, loadAuditLogs, loadUsers]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadStats(), 
        loadSystemHealth(), 
        loadAuditLogs({ limit: 8 }),
        loadUsers()
      ]);
      setLastRefreshed(new Date());
      toast.success('Admin dashboard refreshed');
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Compute "Needs attention" items from live DB state
  const pendingInvites = stats?.users?.pending ?? 0;
  const usersWithoutRoles = Array.isArray(users) ? users.filter((u) => !u.roles || u.roles.length === 0).length : 0;
  const failedLogins = stats?.audit?.failed_24h ?? 0;
  const degradedChecks = Array.isArray(systemHealth?.checks) 
    ? systemHealth.checks.filter((c) => c.status !== 'healthy') 
    : [];
  const needsAttentionCount = 
    (pendingInvites > 0 ? 1 : 0) + 
    (usersWithoutRoles > 0 ? 1 : 0) + 
    (failedLogins > 0 ? 1 : 0) + 
    (degradedChecks.length > 0 ? 1 : 0);

  // Filter permission & role related logs for the activity feed
  const recentChanges = Array.isArray(auditLogs) ? auditLogs.slice(0, 6) : [];

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
      {/* Shared Standard Page Header */}
      <AdminPageHeader
        icon={LayoutDashboard}
        title="Admin Overview"
        subtitle="Centralized platform governance, unified RBAC, system telemetry and security posture"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
        action={
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => navigate('/admin/users')}
            className="flex items-center gap-1.5"
          >
            <Users className="size-3.5" />
            <span>Manage Users</span>
          </Button>
        }
      />

      <div className="space-y-4">
        {/* 1. Four Standardized KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminMetricCard
            title="Total Users"
            value={stats?.users?.total ?? 0}
            subLabel={`${stats?.users?.active ?? 0} active · ${stats?.users?.pending ?? 0} pending account invitations`}
            icon={Users}
            badge={{ label: 'Directory', variant: 'secondary' }}
            onClick={() => navigate('/admin/users')}
            isLoading={isLoading && !stats}
          />
          <AdminMetricCard
            title="Active Roles"
            value={stats?.roles?.total ?? 0}
            subLabel={`${stats?.roles?.system ?? 0} system roles · ${stats?.roles?.custom ?? 0} custom defined`}
            icon={Shield}
            badge={{ label: 'RBAC', variant: 'secondary' }}
            onClick={() => navigate('/admin/roles')}
            isLoading={isLoading && !stats}
          />
          <AdminMetricCard
            title="Platform Services"
            value={`${stats?.modules?.platform ?? 4} Active`}
            subLabel="Support, Assets, Admin Services"
            icon={Layers}
            badge={{ label: 'Operational', variant: 'success' }}
            onClick={() => navigate('/admin/permissions')}
            isLoading={isLoading && !stats}
          />
          <AdminMetricCard
            title="Platform Permissions"
            value={stats?.permissions?.platform ?? stats?.permissions?.in_scope ?? 158}
            subLabel={`Of ${stats?.permissions?.total ?? 410} total platform-wide permissions`}
            icon={Key}
            badge={{ label: 'Granular', variant: 'info' }}
            onClick={() => navigate('/admin/matrix')}
            isLoading={isLoading && !stats}
          />
        </div>

        {/* 2. "Needs Attention" Panel */}
        <Card className="border-border bg-card shadow-sm overflow-hidden">
          <CardHeader className="p-4 py-3 bg-secondary/30 border-b border-border flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`size-4 ${needsAttentionCount > 0 ? 'text-warning' : 'text-success'}`} />
              <h2 className="text-sm font-semibold font-display text-foreground">
                Needs Attention
              </h2>
            </div>
            <Badge 
              variant="outline" 
              className={`text-[11px] ${
                needsAttentionCount > 0 
                  ? 'bg-warning/10 text-warning border-warning/30 font-medium' 
                  : 'bg-success/10 text-success border-success/30 font-medium'
              }`}
            >
              {needsAttentionCount > 0 ? `${needsAttentionCount} items flagged` : 'All systems clear'}
            </Badge>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Pending Invites */}
              <div 
                onClick={() => navigate('/admin/users?status=pending')}
                className="p-3 rounded-lg border border-border bg-card hover:bg-secondary/40 cursor-pointer transition-colors space-y-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Pending Invites</span>
                  <UserCheck className="size-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold font-display ${pendingInvites > 0 ? 'text-warning' : 'text-foreground'}`}>
                    {pendingInvites}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {pendingInvites === 1 ? 'user awaiting' : 'users awaiting'}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {pendingInvites > 0 ? 'Unclaimed invitations' : 'All accounts claimed'}
                </p>
              </div>

              {/* Users With No Roles */}
              <div 
                onClick={() => navigate('/admin/users?role=none')}
                className="p-3 rounded-lg border border-border bg-card hover:bg-secondary/40 cursor-pointer transition-colors space-y-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Unassigned Roles</span>
                  <UserX className="size-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold font-display ${usersWithoutRoles > 0 ? 'text-destructive' : 'text-foreground'}`}>
                    {usersWithoutRoles}
                  </span>
                  <span className="text-[11px] text-muted-foreground">no permissions</span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {usersWithoutRoles > 0 ? 'Requires role assignment' : 'All users have roles'}
                </p>
              </div>

              {/* Failed Logins (24h) */}
              <div 
                onClick={() => navigate('/admin/audit')}
                className="p-3 rounded-lg border border-border bg-card hover:bg-secondary/40 cursor-pointer transition-colors space-y-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Failed Logins (24h)</span>
                  <AlertCircle className="size-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold font-display ${failedLogins > 0 ? 'text-warning' : 'text-foreground'}`}>
                    {failedLogins}
                  </span>
                  <span className="text-[11px] text-muted-foreground">events logged</span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {failedLogins > 0 ? 'Investigate in audit log' : 'Zero auth anomalies'}
                </p>
              </div>

              {/* Degraded Services */}
              <div 
                onClick={() => navigate('/admin/system')}
                className="p-3 rounded-lg border border-border bg-card hover:bg-secondary/40 cursor-pointer transition-colors space-y-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Service Health</span>
                  <Server className="size-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold font-display ${degradedChecks.length > 0 ? 'text-destructive' : 'text-success'}`}>
                    {degradedChecks.length}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {degradedChecks.length > 0 ? 'warnings' : 'degraded'}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {degradedChecks.length > 0 ? `${degradedChecks.length} check requiring check` : 'All health checks passing'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Two-Column Layout: Live System Health & Governance Hub */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column (7 cols): Live System Checks & Diagnostics */}
          <div className="lg:col-span-7 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-section-title font-semibold font-display text-foreground flex items-center gap-2">
                <Server className="size-4 text-primary" />
                <span>Live System Checks</span>
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/system')}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 h-8 px-2"
              >
                <span>Full Telemetry</span>
                <ArrowUpRight className="size-3" />
              </Button>
            </div>

            <Card className="bg-card border-border shadow-sm">
              <CardHeader className="p-4 pb-3 border-b border-border flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Infrastructure Diagnostics
                  </span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-success/10 border border-success/30 text-success text-[11px] font-medium">
                  <span className="size-1.5 rounded-full bg-success animate-pulse" />
                  <span>{systemHealth?.overallText || 'Operational'}</span>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {systemHealth?.checks && systemHealth.checks.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {systemHealth.checks.map((check) => (
                      <div 
                        key={check.id} 
                        className="p-3 rounded-lg border border-border bg-secondary/20 flex items-start justify-between gap-2 transition-colors hover:border-border/80"
                      >
                        <div className="space-y-1 min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {check.category === 'Database' ? (
                              <Database className="size-3.5 text-primary shrink-0" />
                            ) : (
                              <Server className="size-3.5 text-primary shrink-0" />
                            )}
                            <span className="text-xs font-semibold text-foreground truncate">{check.name}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate">{check.details || 'Operational'}</p>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] shrink-0 font-medium ${
                            check.status === 'healthy' 
                              ? 'bg-success/10 text-success border-success/30' 
                              : 'bg-warning/10 text-warning border-warning/30'
                          }`}
                        >
                          {check.latencyMs ? `${check.latencyMs}ms` : check.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    Connecting to telemetry agents...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Audit & Permission Feed */}
            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-section-title font-semibold font-display text-foreground flex items-center gap-2">
                  <FileText className="size-4 text-primary" />
                  <span>Recent Permission Changes</span>
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/admin/audit')}
                  className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 h-8 px-2"
                >
                  <span>View Full Audit</span>
                  <ArrowUpRight className="size-3" />
                </Button>
              </div>

              <Card className="bg-card border-border shadow-sm divide-y divide-border">
                {recentChanges.length > 0 ? (
                  recentChanges.map((item) => (
                    <div key={item.id} className="p-3.5 flex items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="size-7 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <Key className="size-3.5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {item.action || 'Permission Action'}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            By {item.user_email || 'System'} · {item.resource_type || 'Resource'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 text-muted-foreground text-[11px]">
                        <Clock className="size-3" />
                        <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Recent'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No recent permission alterations recorded.
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Right Column (5 cols): Governance Hub Navigation & Quick Actions */}
          <div className="lg:col-span-5 space-y-3">
            <h2 className="text-section-title font-semibold font-display text-foreground flex items-center gap-2">
              <Shield className="size-4 text-primary" />
              <span>Governance Hub</span>
            </h2>

            <Card className="bg-card border-border shadow-sm divide-y divide-border">
              {[
                {
                  title: 'User Management',
                  desc: 'Directory, employee accounts, invitations & role mapping',
                  icon: Users,
                  href: '/admin/users',
                },
                {
                  title: 'Roles & Hierarchy',
                  desc: 'Authority levels (100–10) and service delegation rules',
                  icon: Shield,
                  href: '/admin/roles',
                },
                {
                  title: 'Permissions & Services',
                  desc: 'Service-scoped resource and action bindings',
                  icon: Key,
                  href: '/admin/permissions',
                },
                {
                  title: 'Permission Matrix',
                  desc: 'Visual Role × Service action comparison & editing matrix',
                  icon: Layers,
                  href: '/admin/matrix',
                },
                {
                  title: 'Service Access & Security',
                  desc: 'SSO providers, MFA enforcement and session policies',
                  icon: Lock,
                  href: '/admin/access',
                },
                {
                  title: 'API & Integrations',
                  desc: 'Scoped API tokens, webhooks and service connectivity',
                  icon: Server,
                  href: '/admin/api',
                },
                {
                  title: 'Audit Logs & Diff History',
                  desc: 'Immutable security log and before/after state diff inspector',
                  icon: FileText,
                  href: '/admin/audit',
                },
              ].map((item, idx) => {
                const ItemIcon = item.icon;
                return (
                  <div 
                    key={idx} 
                    onClick={() => navigate(item.href)}
                    className="p-3.5 flex items-center justify-between hover:bg-secondary/40 cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <ItemIcon className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xs font-semibold text-foreground font-display group-hover:text-primary transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-[11px] text-muted-foreground truncate">{item.desc}</p>
                      </div>
                    </div>
                    <ArrowRight className="size-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
                  </div>
                );
              })}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
